/**
 * 主檔 · 新增貨品表單（`ui-spec.md` 頁 15 下半）
 *
 * ⚠️ cbm **一律由 `@coeldery/sku-schema` 計**（後端唯一公式）→ 呢個表單只收原始尺寸，
 *    計出嚟嘅 cbm 由列表顯示（前端**唔准**自己乘長闊高，否則兩個公式會飄）。
 */
import { useEffect, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import FormSelectField from '@coeldery/form-select-field';
import FormTextField from '@coeldery/form-text-field';
import { btnPrimary, errText, hint as hintStyle } from '@coeldery/ui-kit';
import { ApiError, api, type Item, type Supplier } from '../lib/api';

export interface ItemCreateFormProps {
  suppliers: Supplier[];
  /**
   * 新增成功後叫爸爸 reload；⭐ 會傳返新貨品（`Item`）—— 爸爸可以即刻加佢入發貨單行
   * （產品負責人 2026-09-28：「建完貨品就應該有數量／入到單」）
   */
  onCreated: (created?: Item) => Promise<void>;
  /**
   * ⭐ 鎖死供應商（供應商自己嘅「我嘅貨品」用）：傳咗就**唔顯示**供應商選擇器，
   * 一律用呢個 id（後端亦只信 session，唔信 body → 呢個只係介面簡化）。
   */
  lockedSupplierId?: string;
}

interface FormState {
  supplierId: string;
  name: string;
  barcode: string;
  unitsPerCarton: string;
  cartonLCm: string;
  cartonWCm: string;
  cartonHCm: string;
  cbmOverride: string;
  weightKg: string;
}

const BLANK: FormState = {
  supplierId: '',
  name: '',
  barcode: '',
  unitsPerCarton: '',
  cartonLCm: '',
  cartonWCm: '',
  cartonHCm: '',
  cbmOverride: '',
  weightKg: '',
};

/** 空字串 → null（後端「未填」用 NULL；`0` 唔可以當未填） */
const numOrNull = (v: string): number | null => {
  const s = v.trim();
  if (!s) return null;
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
};

export default function ItemCreateForm({ suppliers, onCreated, lockedSupplierId }: ItemCreateFormProps) {
  const { t } = useTranslation();
  const [form, setForm] = useState<FormState>({ ...BLANK, supplierId: lockedSupplierId ?? '' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  /**
   * ⭐ 2026-09-28 修 bug（產品負責人實測「撳『新增貨品』冇反應」）：
   *   下拉選單**冇空選項**時，畫面睇落揀咗供應商，但 state 仍然係 `''` →
   *   提交掣 `disabled={!form.supplierId}` 一直灰住 → 撳落去**完全冇反應**（連錯誤訊息都冇）。
   *   → 修法分兩種情況（唔想 admin 誤揀錯供應商）：
   *     · **只有一個供應商**（＝供應商自己）：自動揀佢 → 零摩擦
   *     · **多過一個**（＝admin）：加一個「請選擇」空選項，令畫面同 state 一致 ＋ 顯示提示文字
   */
  useEffect(() => {
    if (lockedSupplierId || form.supplierId) return;
    if (suppliers.length === 1) setForm((f) => ({ ...f, supplierId: suppliers[0].id }));
  }, [suppliers, lockedSupplierId, form.supplierId]);

  const set = (key: keyof FormState) => (v: string | null) =>
    setForm((f) => ({ ...f, [key]: v ?? '' }));

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    setDone(false);
    try {
      const res = await api.createItem({
        supplierId: lockedSupplierId || form.supplierId,
        name: form.name.trim(),
        barcode: form.barcode.trim(),
        unitsPerCarton: Number(form.unitsPerCarton),
        cartonLCm: Number(form.cartonLCm),
        cartonWCm: Number(form.cartonWCm),
        cartonHCm: Number(form.cartonHCm),
        cbmPerCartonOverride: numOrNull(form.cbmOverride),
        weightKgPerCarton: numOrNull(form.weightKg),
      });
      setForm({ ...BLANK, supplierId: lockedSupplierId ?? form.supplierId }); // 保留供應商 → 連續入貨快啲
      setDone(true);
      // ⭐ 傳返新貨品畀爸爸（例：發貨單頁即刻加佢入行）
      await onCreated(res.item);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.loadFailed'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} style={{ display: 'grid', gap: '12px' }}>
      {!lockedSupplierId && (
        <FormSelectField
          id="f-item-supplier"
          labelKey="master.supplier"
          value={form.supplierId}
          onChange={(v) => setForm((f) => ({ ...f, supplierId: v }))}
          options={[
            // ⭐ 多過一個供應商時一定要有「請選擇」空選項 —— 否則畫面同 state 唔一致（2026-09-28 修）
            ...(suppliers.length > 1 ? [{ value: '', label: t('po.pick') }] : []),
            ...suppliers.map((s) => ({ value: s.id, label: `${s.code} ${s.name}` })),
          ]}
        />
      )}
      <FormTextField
        id="f-item-name"
        labelKey="master.itemName"
        value={form.name}
        onChange={set('name')}
      />
      <FormTextField
        id="f-item-barcode"
        labelKey="master.barcode"
        value={form.barcode}
        onChange={set('barcode')}
        hintKey="master.hintBarcode"
      />
      <FormTextField
        id="f-item-upc"
        labelKey="master.unitsPerCarton"
        type="number"
        value={form.unitsPerCarton}
        onChange={set('unitsPerCarton')}
      />
      <FormTextField
        id="f-item-l"
        labelKey="master.cartonL"
        type="number"
        value={form.cartonLCm}
        onChange={set('cartonLCm')}
        hintKey="master.hintCbm"
      />
      <FormTextField
        id="f-item-w"
        labelKey="master.cartonW"
        type="number"
        value={form.cartonWCm}
        onChange={set('cartonWCm')}
      />
      <FormTextField
        id="f-item-h"
        labelKey="master.cartonH"
        type="number"
        value={form.cartonHCm}
        onChange={set('cartonHCm')}
      />
      <FormTextField
        id="f-item-cbm"
        labelKey="master.cbmOverride"
        type="number"
        value={form.cbmOverride}
        onChange={set('cbmOverride')}
        hintKey="master.hintOverride"
      />
      <FormTextField
        id="f-item-weight"
        labelKey="master.weightKg"
        type="number"
        value={form.weightKg}
        onChange={set('weightKg')}
      />
      <div>
        <button type="submit" style={btnPrimary} disabled={busy || !form.supplierId}>
          {busy ? t('common.adding') : t('master.addItem')}
        </button>
        {/* ⭐ 講清楚點解撳唔到（唔好淨係灰住個掣令用戶以為「冇反應」） */}
        {!lockedSupplierId && !form.supplierId && (
          <p style={errText}>{t('master.pickSupplierFirst')}</p>
        )}
      </div>
      {error && (
        <p style={errText} role="alert">
          {error}
        </p>
      )}
      {done && (
        <p style={{ ...hintStyle, color: 'var(--color-primary)', fontWeight: 700 }}>
          {t('master.itemCreated')}
        </p>
      )}
    </form>
  );
}
